'use client';

import type { Dispatch, SetStateAction } from 'react';

const DEFAULT_SCALE = 1;
const VIEWPORT_UNIT_DIVISOR = 100;

export interface ViewportMetrics {
  width: number;
  height: number;
  rawWidth: number;
  rawHeight: number;
  vw: number;
  vh: number;
  scale: number;
  inverseScale: number;
  dpr: number;
  ready: boolean;
}

export interface RawViewportDimensions {
  rawWidth: number;
  rawHeight: number;
}

interface ClampPositiveInput {
  value: number;
  fallback: number;
}

interface RawViewportDimensionInput {
  axis: ViewportDimensionAxis;
}

interface ViewportDimensionRule {
  getVisualViewportValue: (visualViewport: VisualViewport | null) => number | undefined;
  getWindowValue: () => number;
}

interface CommitViewportMetricsInput {
  setMetrics: Dispatch<SetStateAction<ViewportMetrics>>;
  nextMetrics: ViewportMetrics;
}

enum ViewportDimensionAxis {
  Width = 'width',
  Height = 'height',
}

const EMPTY_DEVICE_PIXEL_RATIO = 1;

export const EMPTY_VIEWPORT: ViewportMetrics = {
  width: 0,
  height: 0,
  rawWidth: 0,
  rawHeight: 0,
  vw: 0,
  vh: 0,
  scale: DEFAULT_SCALE,
  inverseScale: DEFAULT_SCALE,
  dpr: EMPTY_DEVICE_PIXEL_RATIO,
  ready: false,
};

const VIEWPORT_METRIC_KEYS: ReadonlyArray<keyof ViewportMetrics> = [
  'width',
  'height',
  'rawWidth',
  'rawHeight',
  'vw',
  'vh',
  'scale',
  'inverseScale',
  'dpr',
  'ready',
];

const VIEWPORT_DIMENSION_RULE: Record<ViewportDimensionAxis, ViewportDimensionRule> = {
  [ViewportDimensionAxis.Width]: {
    getVisualViewportValue: (visualViewport) => visualViewport?.width,
    getWindowValue: () => window.innerWidth,
  },
  [ViewportDimensionAxis.Height]: {
    getVisualViewportValue: (visualViewport) => visualViewport?.height,
    getWindowValue: () => window.innerHeight,
  },
};

export function measureRawViewportDimensions(): RawViewportDimensions {
  return {
    rawWidth: getRawViewportDimension({ axis: ViewportDimensionAxis.Width }),
    rawHeight: getRawViewportDimension({ axis: ViewportDimensionAxis.Height }),
  };
}

export function buildViewportMetrics({
  rawWidth,
  rawHeight,
}: RawViewportDimensions): ViewportMetrics {
  const bodyComputedZoom = Number.parseFloat(
    getComputedStyle(document.body).zoom || String(DEFAULT_SCALE),
  );
  const scale = clampPositive({ value: bodyComputedZoom, fallback: DEFAULT_SCALE });
  const inverseScale = 1 / scale;
  const width = rawWidth * inverseScale;
  const height = rawHeight * inverseScale;

  return {
    width,
    height,
    rawWidth,
    rawHeight,
    vw: width / VIEWPORT_UNIT_DIVISOR,
    vh: height / VIEWPORT_UNIT_DIVISOR,
    scale,
    inverseScale,
    dpr: clampPositive({
      value: window.devicePixelRatio || DEFAULT_SCALE,
      fallback: DEFAULT_SCALE,
    }),
    ready: true,
  };
}

export function commitViewportMetrics({
  setMetrics,
  nextMetrics,
}: CommitViewportMetricsInput): void {
  setMetrics((currentMetrics) => (
    areViewportMetricsEqual({ currentMetrics, nextMetrics }) ? currentMetrics : nextMetrics
  ));
}

function getRawViewportDimension({ axis }: RawViewportDimensionInput): number {
  const rule = VIEWPORT_DIMENSION_RULE[axis];
  const fallback = rule.getWindowValue();
  const value = rule.getVisualViewportValue(window.visualViewport) ?? fallback;

  return clampPositive({ value, fallback: fallback || 0 });
}

function clampPositive({ value, fallback }: ClampPositiveInput): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function areViewportMetricsEqual({
  currentMetrics,
  nextMetrics,
}: {
  currentMetrics: ViewportMetrics;
  nextMetrics: ViewportMetrics;
}): boolean {
  return VIEWPORT_METRIC_KEYS.every(
    (metricKey) => currentMetrics[metricKey] === nextMetrics[metricKey],
  );
}
